import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "../../../supabase/supabase";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface ProStatusToggleProps {
  organizationId: string;
  initialProStatus: boolean;
  onStatusChange?: (newStatus: boolean) => void;
}

export default function ProStatusToggle({
  organizationId,
  initialProStatus,
  onStatusChange,
}: ProStatusToggleProps) {
  const [isProAccount, setIsProAccount] = useState(initialProStatus);
  const [isLoading, setIsLoading] = useState(false);

  const toggleProStatus = async () => {
    if (!organizationId) return;

    setIsLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (!accessToken) {
        throw new Error("Authentication required. Please log in again.");
      }

      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/toggle_pro_status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "x-supabase-anon-key": supabaseAnonKey,
          },
          body: JSON.stringify({
            organizationId,
          }),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to toggle PRO status");
      }

      setIsProAccount(result.is_pro);
      if (onStatusChange) {
        onStatusChange(result.is_pro);
      }

      toast({
        title: result.is_pro
          ? "PRO Account Activated"
          : "Downgraded to Standard Account",
        description: result.is_pro
          ? "Your organization now has access to all PRO features."
          : "Your organization has been downgraded to a standard account.",
        variant: "default",
      });
    } catch (error: any) {
      console.error("Error toggling PRO status:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to toggle PRO status",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="rounded-none">
      <CardHeader>
        <CardTitle>Account Type</CardTitle>
        <CardDescription>
          Manage your organization's account type and access to features
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="pro-status">PRO Account</Label>
              <p className="text-sm text-muted-foreground">
                {isProAccount
                  ? "Your organization has access to all PRO features"
                  : "Upgrade to PRO for access to all features"}
              </p>
            </div>
            <div className="flex items-center">
              {isProAccount ? (
                <CheckCircle2 className="mr-2 h-4 w-4 text-green-500" />
              ) : (
                <AlertCircle className="mr-2 h-4 w-4 text-amber-500" />
              )}
              <Switch
                id="pro-status"
                checked={isProAccount}
                onCheckedChange={toggleProStatus}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="rounded-none bg-muted p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                {isLoading ? (
                  <Loader2 className="h-5 w-5 text-primary animate-spin" />
                ) : isProAccount ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                )}
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium">
                  {isLoading
                    ? "Processing..."
                    : isProAccount
                      ? "PRO Account Active"
                      : "Standard Account"}
                </h3>
                <div className="mt-2 text-sm text-muted-foreground">
                  <p>
                    {isProAccount
                      ? "Your organization has access to all features including advanced assistant configuration, function calling, and more."
                      : "Standard accounts have limited access to features. Upgrade to PRO for full access."}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Button
            onClick={toggleProStatus}
            variant={isProAccount ? "outline" : "default"}
            className={
              isProAccount
                ? "rounded-none"
                : "bg-primary text-primary-foreground rounded-none"
            }
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : isProAccount ? (
              "Downgrade to Standard"
            ) : (
              "Upgrade to PRO"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
